import { HelpCircle, AlertCircle, FileText } from 'lucide-react';
import React, { useState } from 'react';
import { useUploadInvoiceValidationFileMutation, useRunInvoiceGroupValidationMutation } from 'src/redux/billing/billing.endpoints';
import { useGetPlatformsQuery } from 'src/redux/admin/reporting/reporting.endpoints';
import { InvoiceGroupDetails, ValidationFile } from 'src/redux/billing/billing.types';
import { Button } from 'src/components/ui/button';

interface PendingFile {
  file: File | null;
  source: string;
}

interface ValidateUploadContainerProps {
  invoiceGroupId: string;
  invoiceGroupDetails?: InvoiceGroupDetails | null;
  onUpload?: () => void;
  onValidate?: () => void;
}

const FILE_TYPES = ['.csv'];
const MAX_SIZE_BYTES = 25 * 1024 * 1024;

const formatUploadedAt = (iso: string) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const ValidateUploadContainer: React.FC<ValidateUploadContainerProps> = ({
  invoiceGroupId,
  invoiceGroupDetails,
  onUpload,
  onValidate,
}) => {
  const [pendingRows, setPendingRows] = useState<PendingFile[]>([{ file: null, source: '' }]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);

  const { data: sources = [] } = useGetPlatformsQuery({});
  const [uploadFile, { isLoading: isUploading }] = useUploadInvoiceValidationFileMutation();
  const [runValidation, { isLoading: isRunningValidation }] = useRunInvoiceGroupValidationMutation();

  const validationFiles: ValidationFile[] = invoiceGroupDetails?.validations?.files ?? [];
  const validationErrors: string[] = (invoiceGroupDetails?.errors ?? []).map((e) => e.error);
  const hasUploadedFiles = validationFiles.length > 0;

  const setRowFile = (index: number, file: File | null) => {
    setUploadError(null);
    setPendingRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], file };
      return next;
    });
  };

  const setRowSource = (index: number, source: string) => {
    setPendingRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], source };
      return next;
    });
  };

  const onFileInputChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    if (!f) {
      setRowFile(index, null);
      return;
    }
    const name = f.name.toLowerCase();
    const isCsv = FILE_TYPES.some((type) => name.endsWith(type));
    if (!isCsv) {
      setUploadError(`Please select a ${FILE_TYPES.join(' or ')} file.`);
      setRowFile(index, null);
      return;
    }
    if (f.size > MAX_SIZE_BYTES) {
      setUploadError('File too large. Max 25 MB.');
      setRowFile(index, null);
      return;
    }
    setRowFile(index, f);
  };

  const uploadOne = async (index: number) => {
    const row = pendingRows[index];
    if (!row.file || !row.source) {
      setUploadError('Select a file and a source for this row.');
      return;
    }
    setUploadError(null);
    try {
      await uploadFile({ file: row.file, id: invoiceGroupId, source: row.source }).unwrap();
      setRowFile(index, null);
      setRowSource(index, '');
      setFileInputKey((k) => k + 1);
      onUpload?.();
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'data' in err
        ? String((err as { data?: unknown }).data)
        : 'Upload failed.';
      setUploadError(message);
    }
  };

  const handleRunValidation = async () => {
    setUploadError(null);
    try {
      await runValidation(invoiceGroupId).unwrap();
      onValidate?.();
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'data' in err
        ? String((err as { data?: unknown }).data)
        : 'Validation failed.';
      setUploadError(message);
    }
  };

  return (
    <div className="p-4 pt-2 bg-gray-201 rounded-b-lg">
      <div className="flex flex-row gap-2 items-start mb-3">
        <HelpCircle className="h-4 w-4 shrink-0 mt-0.5" />
        <p className="text-xs text-gray-600">
          Upload one or more CSV files and select a source (platform) for each. Use the
          {' '}
          <a
            href="/csv/validate_invoice_template.csv"
            download="validate_invoice_template.csv"
            className="text-blue-500 underline cursor-pointer"
          >
            template
          </a>
          {' '}
          to get started. Typically 3–4 sources are used for validation.
        </p>
      </div>

      {/* Pending uploads: single file + source row; clears after upload */}
      <div className="space-y-2 mb-4">
        {pendingRows.map((row, index) => (
          <div key={`${index}-${fileInputKey}`} className="flex flex-wrap items-center gap-2">
            <label className="group flex cursor-pointer items-center rounded-md border border-gray-300 bg-gray-50 px-3 py-1.5 text-xs text-gray-700 transition-colors hover:border-gray-400 hover:bg-gray-200 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-1">
              <span className="mr-2 shrink-0">Choose file</span>
              <input
                key={fileInputKey}
                type="file"
                accept=".csv,text/csv"
                className="cursor-pointer text-xs file:mr-2 file:cursor-pointer file:rounded file:border-0 file:bg-gray-200 file:px-2 file:py-1 file:text-gray-700 file:transition-colors group-hover:file:bg-gray-300"
                onChange={(e) => onFileInputChange(index, e)}
              />
            </label>
            <select
              value={row.source || ''}
              onChange={(e) => setRowSource(index, e.target.value)}
              className="h-8 min-w-[180px] rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              aria-label="Select source"
            >
              <option value="">Select source</option>
              {sources.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="sm"
              variant="primary"
              className="h-8 text-xs font-medium"
              onClick={() => uploadOne(index)}
              disabled={!row.file || !row.source || isUploading}
            >
              {isUploading ? 'Uploading…' : 'Upload'}
            </Button>
            {row.file && (
              <span className="text-xs text-gray-500 truncate max-w-[120px]">
                {row.file.name}
              </span>
            )}
          </div>
        ))}
      </div>

      {uploadError && (
        <div className="flex items-center gap-2 mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {uploadError}
        </div>
      )}

      {/* Uploaded files: show each file as a record */}
      {validationFiles.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-medium text-gray-700 mb-2 flex items-center gap-1">
            <FileText className="h-3.5 w-3.5" />
            Uploaded files
          </h4>
          <div className="space-y-2">
            {validationFiles.map((f, i) => (
              <div
                key={i}
                className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 shadow-sm"
              >
                <div className="font-medium text-gray-900">{f.filename}</div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0 text-gray-600">
                  <span>
                    <span className="text-gray-500">Rows:</span>
                    {' '}
                    {f.rowcount}
                  </span>
                  {f.uploaded_at && (
                    <span>
                      <span className="text-gray-500">Uploaded:</span>
                      {' '}
                      {formatUploadedAt(f.uploaded_at)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Validation errors (from invoice group top-level errors) */}
      {validationErrors.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-medium text-red-700 mb-2 flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" />
            Validation errors
          </h4>
          <ul className="text-xs text-red-600 space-y-1 list-disc list-inside max-h-32 overflow-y-auto">
            {validationErrors.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Run validation button */}
      <div className="pt-2 border-t border-gray-200">
        <Button
          type="button"
          size="sm"
          className="bg-green-600 hover:bg-green-700 text-white"
          onClick={handleRunValidation}
          disabled={!hasUploadedFiles || isRunningValidation}
        >
          {isRunningValidation ? 'Running validation…' : 'Run validation'}
        </Button>
        {!hasUploadedFiles && (
          <span className="ml-2 text-xs text-gray-500">
            Upload at least one file to run validation.
          </span>
        )}
      </div>
    </div>
  );
};

export default ValidateUploadContainer;
